/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { PrinterBusinessService, PrinterFinderListItem, REPORT_SUP_SCHEDULE, ReportService } from '@gh/builder-shared';
import { reportField, reportIdField } from '@gh/builder-shared-data';
import { createDialogRoute, FormStateSnapshot, getFormStateSnapshotValue } from '@gh/common-shared';
import {
  DateFormat,
  emptySelectionModel,
  emptySlicedDataSource,
  getDateMapper,
  SelectionModel,
  SlicedDataSource,
} from '@gh/core-data';
import { DialogInput, NotificationService, WaitingService } from '@gh/core-ui';
import { differentFieldsWith } from '@gh/core-util';
import { switchMap$ } from '@gh/rx/operators';
import { flatten, isEmpty } from 'lodash';
import {
  ScheduleListBusinessService,
  ScheduleSupervisorsReportFilterDialogConfig,
} from '../../resources/schedule-list-business.service';
import {
  isScheduleSupervisorFieldsAffectFilter,
  SchedulesSupervisorPrintMode,
  ScheduleSupervisorReportFilter,
  ScheduleSupervisorReportTask,
} from '../../resources/schedule-list.model';
import { ScheduleSupervisorsReportFilterFormState } from './schedule-supervisors-report-filter-form.service';
import { GoogleAuthService } from '../../../core-external';

@Component({
  selector: 'gh-schedule-supervisors-report-filter-dialog',
  template: require('./schedule-supervisors-report-filter-dialog.component.html'),
  styles: [require('./schedule-supervisors-report-filter-dialog.component.scss')],
  providers: [ScheduleSupervisorsReportFilterFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleSupervisorsReportFilterDialogComponent implements OnInit, DialogInput<ScheduleSupervisorsReportFilterDialogConfig> {
  input: ScheduleSupervisorsReportFilterDialogConfig;
  taskDataSource: SlicedDataSource<ScheduleSupervisorReportTask>;
  supplierDataSource: SlicedDataSource<ScheduleSupervisorReportTask>;
  isCloud: boolean;

  constructor(private dialogRef: MatDialogRef<any>,
              private notifications: NotificationService,
              private waitings: WaitingService,
              public form: ScheduleSupervisorsReportFilterFormState,
              private printers: PrinterBusinessService,
              private googleAuth: GoogleAuthService,
              private reports: ReportService,
              private business: ScheduleListBusinessService) {
  }

  ngOnInit(): void {
    this.form.build(this.input);
    this.loadTaskData();
    this.loadPrintersData();
    this.loadSupplierData();
  }

  onTaskFilterChange(taskFilter: FormStateSnapshot<ScheduleSupervisorReportFilter>): void {
    const oldTaskFilter = getFormStateSnapshotValue(this.form.taskFilter);
    const newTaskFilter = getFormStateSnapshotValue(taskFilter);

    this.form.setTaskFilter(taskFilter);

    const differentFields = differentFieldsWith(newTaskFilter, oldTaskFilter);
    if (isScheduleSupervisorFieldsAffectFilter(differentFields)) {
      this.form.resetTaskSelection();
      this.form.resetSupplierSelection();

      const valid = this.form.isTaskFilterValid;
      this.loadTaskData(!valid);
      this.loadSupplierData(!valid);
    }
  }

  onPrintModeChange(printMode: SchedulesSupervisorPrintMode): void {
    this.form.setPrintMode(printMode);
    this.form.resetSupplierSelection();
    this.loadSupplierData();
  }

  onPrinterChange(printer: PrinterFinderListItem): void {
    this.form.setPrinter(printer);
  }

  onFindPrinter(name: string): void {
    this.form.setPrinterName(name);
  }

  onPrintEmailAttachmentsChange(isPrintEmailAttachments: boolean): void {
    this.form.setPrintEmailAttachments(isPrintEmailAttachments);
  }

  onTaskSelectionModelChange(selectionModel: SelectionModel): void {
    this.form.setTaskSelectionModel(selectionModel);
  }

  onTaskExistChange(exists: boolean): void {
    this.form.setTaskSelectionModel(emptySelectionModel(exists));
    this.form.setTaskExists(exists);
  }

  onSupplierSelectionModelChange(selectionModel: SelectionModel): void {
    this.form.setSupplierSelectionModel(selectionModel);
  }

  onSupplierTaskExistChange(exists: boolean): void {
    this.form.setSupplierSelectionModel(emptySelectionModel(exists));
  }

  onBack(): void {
    this.form.goBack();
  }

  onNext(): void {
    this.form.goNext();
  }

  onDeliver(): void {
    this.validateAndSubmitReport();
  }

  onPreview(): void {
    this.showReportDialog(false);
  }

  onShowSupplierList(): void {
    this.showReportDialog(true);
  }

  onFinish(): void {
    this.submitReportWithPrinter();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private validateAndSubmitReport(): void {
    this.business.submitSupervisorScheduleReport({
      details: this.form.value,
      skipDeliveryCheck: false,
    }).pipe(this.waitings.operator())
      .subscribe(
        () => this.closeDialogOnSuccess(),
        (error) => {
          if (error.has('msg_sch_no_printer_email') || error.has('msg_no_printer')) {
            this.tryToFindPrinter(error.has('msg_sch_no_printer_email'));
          } else {
            this.notifications.processAll(error);
          }
        },
      );
  }

  private submitReportWithPrinter(): void {
    this.printers.grantGoogleOfflineAccess().pipe(
      switchMap$((authCode) =>
        this.business.submitSupervisorScheduleReport({
          details: this.form.value,
          skipDeliveryCheck: true,
          authCode: this.googleAuth.accessToken,
          printerId: this.form.printerId,
        }).pipe(this.waitings.operator())))
      .subscribe(
        () => this.closeDialogOnSuccess(),
        this.notifications.processAll);
  }

  private submitReportWithoutPrinter(): void {
    this.business.submitSupervisorScheduleReport({
      details: this.form.value,
      skipDeliveryCheck: true,
    }).pipe(this.waitings.operator())
      .subscribe(
        () => this.closeDialogOnSuccess(),
        this.notifications.processAll);
  }

  private tryToFindPrinter(optional: boolean): void {
    this.printers.findAllPrinters({
      optional,
      optionalMessage: 'msg_sch_no_printer_email',
    }).subscribe(
      (printers) => {
        if (isEmpty(printers)) {
          this.submitReportWithoutPrinter();
        } else {
          this.form.setPrinters(printers);
          this.form.goNext();
        }
      },
      this.notifications.processAll);
  }

  private loadTaskData(useEmptyDataSource: boolean = false): void {
    // tslint:disable-next-line:max-line-length
    this.taskDataSource = useEmptyDataSource
      ? emptySlicedDataSource()
      : this.business.findAllSupervisorTasksAsDataSource(getFormStateSnapshotValue(this.form.taskFilter));
  }

  private loadPrintersData(): void {
    this.printers.getPrinters().subscribe((res) => {
      this.isCloud = res.cloud;
    });
  }

  private loadSupplierData(useEmptyDataSource: boolean = false): void {
    this.supplierDataSource = useEmptyDataSource
      ? emptySlicedDataSource()
      : this.business.findAllSupervisorTasksAsDataSource(
        getFormStateSnapshotValue(this.form.taskFilter),
        this.form.printMode);
  }

  private closeDialogOnSuccess(): void {
    this.notifications.success('msg_sup_schedule_tasks_added');
    this.dialogRef.close({});
  }

  private showReportDialog(isSendersList: boolean): void {
    const selectionModel = isSendersList ? this.form.supplierSelectionModel : this.form.taskSelectionModel;
    const taskFilter = getFormStateSnapshotValue(this.form.taskFilter);
    const printMode = this.form.printMode;

    this.reports.generateUrlAndShowDialog(REPORT_SUP_SCHEDULE, flatten([
      reportField('WhichSupervisor', taskFilter.isAllSupervisors ? 'All' : 'Selected'),
      !taskFilter.isAllSupervisors && taskFilter.supervisor
        ? [reportIdField('Supervisor', taskFilter.supervisor.userId)]
        : [],
      reportField('From', getDateMapper(DateFormat.Date).serialize(taskFilter.startDate)),
      reportField('To', getDateMapper(DateFormat.Date).serialize(taskFilter.endDate)),
      reportField('IncludeUnsentOrders', taskFilter.showUnproduced),
      reportField('ShowCriticalPathDescr', taskFilter.showCriticalPathDescription),
      reportField('ShowZeroCostCenters', taskFilter.showZeroCost),
      reportField('OrderBy', {
        'startDate': 'StartDate',
        'costCenterCode': 'CostCenter',
      }[taskFilter.orderBy]),
      reportField('NewPageForActivity', taskFilter.isNewPageForEachActivity),
      reportField('NewPageForStartDate', taskFilter.isNewPageForStartDate),
      selectionModel.idList.map((id) => reportIdField('Tasks', id)),
      reportField('IsSendersList', isSendersList),
      !isSendersList || printMode === SchedulesSupervisorPrintMode.Both
        ? [] : [reportField('IsPrinted', printMode === SchedulesSupervisorPrintMode.Printed)],
      reportField('ExcludeMode', selectionModel.isExcludeMode),
    ])).subscribe();
  }
}

export const scheduleSupervisorsReportFilterDialogRoute = createDialogRoute({
  component: ScheduleSupervisorsReportFilterDialogComponent,
});
